import { useEffect, useMemo, useState } from "react"

import { Col, Flex, Row, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import DashboardCard from "../../../Components/DashboardCard"
import EmptyComponent from "../../../Components/Empty"
import SearchComponent from "../../../Components/Search"
import TableComponent from "../../../Components/Table"
import { skeletonColumns, skeletonRows } from "../../../Components/Table/skeleton"
import { RoutesPaths } from "../../../constants/user-routes"
import useDebounce from "../../../hooks/use-debounce"
import Icon from "../../../Icon"
import DeflectFilters from "../components/DeflectFilters"
import {
  REPEAT_SUMMARY_CELLS,
  REPEAT_TABLE_SCROLL,
  repeatColumns
} from "../components/repeatColumns"
import { withValueSkeleton } from "../components/Skeleton/cardSkeleton"
import SummaryRows from "../components/SummaryRows/SummaryRows"
import { useDeflectScope } from "../hooks/useDeflectScope"
import { usePageOf } from "../hooks/usePageOf"
import { DEFLECT_SECTION } from "../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../redux/deflectAnalytics.slice"

import "../../DashboardPage/styles.less"
import tables from "../tables.module.less"

const { Title, Text } = Typography

const PAGE_SIZE = 20
const DEFAULT_ORDERING = "-lookups_count"

// Every repeat cardholder behind the card's "View all": the card shows the top five, this is the
// whole tail, paged, sorted and searched on the server. The medal stays with the card's rank by
// volume, so re-sorting by another column does not renumber anyone.
const RepeatCardholdersFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getCompare } = useDeflectScope()
  const repeat = useSelector(selectDeflectSection(DEFLECT_SECTION.REPEAT_CARDHOLDERS_PAGE))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.REPEAT_CARDHOLDERS_PAGE))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.REPEAT_CARDHOLDERS_PAGE))
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)

  const [ordering, setOrdering] = useState(DEFAULT_ORDERING)
  // Card search, debounced: every search is a request.
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search.trim(), 300)

  // `getCompare` changes with the window and the programs, which make a new query too: it is
  // what sends the table back to page 1 when the filters above move.
  const query = useMemo(
    () => ({ ordering, ...(debouncedSearch ? { search: debouncedSearch } : {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordering, debouncedSearch, getCompare]
  )
  const [page, setPage] = usePageOf(query)

  useEffect(() => {
    getCompare(DEFLECT_SECTION.REPEAT_CARDHOLDERS_PAGE, { ...query, page, page_size: PAGE_SIZE })
  }, [getCompare, query, page])

  const rows = repeat?.results ?? []
  const summary = repeat?.summary

  // The search narrows the table only: the cards keep reporting the whole period.
  const cards = useMemo(
    () => [
      { title: "Repeat cards", value: summary?.cards_count ?? 0, icon: "credit_card" },
      { title: "Their lookups", value: summary?.lookups_count ?? 0, icon: "receipt_check" },
      { title: "Order value", value: summary?.order_amount ?? 0, prefix: "$", icon: "money" },
      {
        title: "Match rate for them",
        value: summary?.match_rate == null ? null : Math.round(summary.match_rate),
        suffix: "%",
        icon: "pulse"
      }
    ],
    [summary]
  )

  const columns = useMemo(() => repeatColumns({ t, sortable: true }), [t])
  const loadingColumns = useMemo(() => skeletonColumns(columns), [columns])

  const handleTableChange = (pagination, _filters, sorter) => {
    const { field, order } = (Array.isArray(sorter) ? sorter[0] : sorter) || {}
    setOrdering(order ? `${order === "descend" ? "-" : ""}${field}` : DEFAULT_ORDERING)
    setPage(pagination.current)
  }

  useEffect(() => {
    document.title = "Repeat cardholders - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <Row gutter={[0, isMobile ? 12 : 32]} className="dashboard-page">
      <Col span={24}>
        <ButtonComponent
          type="text"
          onClick={() => navigate(RoutesPaths.deflect.analytics)}
          icon={<Icon type="chevron_left_small" />}
          style={{ marginLeft: -12 }}
        >
          {t("Back to Analytics")}
        </ButtonComponent>
      </Col>

      <Col span={24}>
        <Flex
          justify="space-between"
          align={!isMobile && "flex-start"}
          style={{ flexDirection: isMobile && "column" }}
        >
          <div style={{ paddingBottom: isMobile && 24 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {t("Repeat cardholders")}
            </Title>
            <Text type="secondary">
              {t(
                "Cards that generated more than one bank inquiry in this period, ranked by volume."
              )}
            </Text>
          </div>

          <DeflectFilters />
        </Flex>
      </Col>

      <Col span={24}>
        <Row gutter={[24, 24]}>
          {(firstLoad ? withValueSkeleton(cards) : cards).map((card) => (
            <Col key={card.title} xs={24} sm={12} lg={6}>
              <DashboardCard data={card} theme={theme} />
            </Col>
          ))}
        </Row>
      </Col>

      <Col span={24}>
        <Flex vertical gap={16} className="breakdown-table-header">
          <Title level={5} style={{ marginBottom: 0 }}>
            {t("Card breakdown")}
          </Title>
          <div className="breakdown-search">
            <SearchComponent
              handleSearch={(e) => setSearch(e.target.value)}
              value={search}
              placeholder={t("Search card")}
            />
          </div>
        </Flex>

        <TableComponent
          className={tables.olympics}
          dataSource={firstLoad ? skeletonRows(PAGE_SIZE) : rows}
          columns={firstLoad ? loadingColumns : columns}
          loading={fetching && !firstLoad}
          withOutPagination={firstLoad}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: repeat?.count ?? 0,
            showSizeChanger: false
          }}
          onChange={handleTableChange}
          scroll={REPEAT_TABLE_SCROLL}
          rowKey={firstLoad ? "key" : "sequence_number"}
          tableLayout="fixed"
          showHeader={firstLoad || !!rows.length}
          summary={() => (
            <SummaryRows
              summary={rows.length && !firstLoad ? summary : null}
              comparisonSummary={repeat?.comparison_summary}
              comparisonLabel={repeat?.comparison_period?.label}
              cells={REPEAT_SUMMARY_CELLS}
            />
          )}
          locale={{
            emptyText: search ? (
              <EmptyComponent type="search" style={{ minHeight: 450 }} />
            ) : (
              <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
            )
          }}
        />
      </Col>
    </Row>
  )
}

export default RepeatCardholdersFullPage
